import React, { useRef, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import SendIcon from '@mui/icons-material/Send'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import type { ChatMessage, ChatPaneProps } from './types'

// The types live in ./types; re-exported so existing imports keep working.
export type { ChatMessage, ToolCall } from './types'
export type ChatSessionProps = ChatPaneProps

// ChatSession is the old chat pane. Every drawer now draws with SidePaneChat
// through ChatDrawer; this stays only for MultiTableAnalysisDrawer, which is
// being switched off (#1474).
const ChatSession: React.FC<ChatSessionProps> = ({
  messages,
  onSendMessage,
  onResetChat,
  onClose,
  isLoading = false,
  error,
  loadingTitle,
  showHeader = true,
  placeholder = 'Ask about this table...',
  showAdvancedMode = true,
  examplePrompts = [],
  autoTriggerExamplePrompts = false,
  disabled = false,
  renderMessage,
  personas = ['Executive Summarizer', 'Table Analyst'],
  selectedPersona = 'Executive Summarizer',
  onPersonaChange,
  additionalControls,
  inputHelper,
  scopeLabel,
  framing,
}) => {
  // Comment: Keep scroll pinned to newest message
  const endRef = useRef<HTMLDivElement>(null)
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  const [mode, setMode] = useState<'normal' | 'advanced'>('normal')

  const currentModel = mode === 'advanced' ? 'gpt-5' : 'gpt-4.1'

  return (
    <>
      {
        showHeader && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider', gap: 2 }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src="/img/platinum-icon.png" alt="Insights" sx={{ width: 32, height: 32 }} />
                {/* The scope is part of the title, not a subtitle: a user must
                    be able to see at a glance which page they are asking
                    about (#974 Stage 1). */}
                {scopeLabel ? `Insights · ${scopeLabel}` : 'Insights'}
              </Typography>
              {
                messages.length > 1 && onResetChat && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      onResetChat?.()
                    }}
                    sx={{
                      textTransform: 'none', // Comment: Remove automatic capitalization
                      fontSize: '0.75em', // Comment: Scale font to 75% of default size
                      padding: '3px 6px', // Comment: Adjust padding proportionally for 75% scaling
                      color: 'primary.main', // Comment: Blue link color
                      textDecoration: 'underline', // Comment: Add underline like a link
                      border: 'none', // Comment: Remove any border
                      minWidth: 'auto', // Comment: Remove minimum width for compact link appearance
                      '&:hover': {
                        textDecoration: 'underline', // Comment: Keep underline on hover
                        backgroundColor: 'transparent' // Comment: No background on hover
                      }
                    }}
                  >
                    reset chat
                  </Button>
                )
              }
            </Box>
            {
              onClose && (
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              )
            }
          </Box>
        )
      }
      
      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {framing && framing.length > 0 && (
          <Box
            data-testid="insights-framing"
            sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}
          >
            {framing.map((line, i) => (
              <Typography key={i} variant="caption" component="div" sx={{ color: 'text.secondary', fontWeight: i === 0 && framing.length > 1 ? 600 : 400 }}>
                {line}
              </Typography>
            ))}
          </Box>
        )}
        {messages.length === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', color: 'text.secondary' }}>
            <Avatar src="/img/platinum-icon.png" alt="Insights" sx={{ width: 48, height: 48, mb: 2 }} />
            {loadingTitle && <Typography variant="body2">{loadingTitle}</Typography>}
          </Box>
        )}

        {messages.map((m, i) => {
          // Check for custom renderer first
          const customRender = renderMessage?.(m, i)
          if (customRender !== undefined) {
            return <React.Fragment key={i}>{customRender}</React.Fragment>
          }

          // Default rendering
          return (
            <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <Box sx={{ display: 'flex', flexDirection: m.role === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-start', gap: 1, maxWidth: '85%' }}>
                {m.role === 'assistant' && <Avatar src="/img/platinum-icon.png" alt="Insights" sx={{ width: 24, height: 24 }} />}
                {m.role === 'user' && <Avatar sx={{ width: 24, height: 24 }}><PersonIcon fontSize="small" /></Avatar>}
                <Card
                  sx={{
                    // bgcolor: m.role === 'user' ? 'primary.light' : '#ffffff',
                    borderRadius: 2,
                    border: m.role === 'user' ? 'none' : '1px solid #00b2ff',
                  }}
                >
                  <CardContent sx={{
                    p: 1.5,
                    '&:last-child': { paddingBottom: 1.5 },
                    '& > :first-of-type': { marginTop: 0 },
                    '& > :last-child': { marginBottom: 0 },
                    '& p': { margin: 0, fontSize: '0.875rem' },
                    '& h1': { fontSize: '1.25rem', fontWeight: 600, marginTop: '0.5em', marginBottom: '0.5em' },
                    '& h2': { fontSize: '1.15rem', fontWeight: 600, marginTop: '0.5em', marginBottom: '0.5em' },
                    '& h3': { fontSize: '1.05rem', fontWeight: 600, marginTop: '0.5em', marginBottom: '0.5em' },
                    '& h4': { fontSize: '0.95rem', fontWeight: 600, marginTop: '0.5em', marginBottom: '0.5em' }
                  }}>
                    {m.role === 'assistant' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    ) : (
                      <Typography variant="body2">{m.content}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )
        })}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src="/img/platinum-icon.png" alt="Insights" sx={{ width: 24, height: 24 }} />
            <Card sx={{ bgcolor: 'grey.100' }}>
              <CardContent sx={{ p: 1.5 }}>
                <CircularProgress size={16} />
              </CardContent>
            </Card>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mx: 1 }}>{error}</Alert>}

        <div ref={endRef} />
      </Box>

      {examplePrompts.length > 0 && (
        <Box sx={{ borderTop: '1px dashed #e0e0e0', borderBottom: '1px dashed #e0e0e0', p: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {examplePrompts.map((prompt, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: '#f5f5f5',
                  cursor: disabled ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                  p: 0.75,
                  borderTop: index > 0 ? '1px solid #e0e0e0' : 'none',
                  opacity: disabled ? 0.5 : 1,
                  '&:hover': {
                    bgcolor: disabled ? '#f5f5f5' : '#eeeeee',
                  },
                }}
                onClick={() => {
                  if (!isLoading && !disabled && autoTriggerExamplePrompts) {
                    onSendMessage(currentModel, prompt, selectedPersona)
                  }
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {prompt}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <ChatInput
        onSendMessage={(model, input) => onSendMessage(model, input, selectedPersona)}
        onResetChat={onResetChat}
        isLoading={isLoading}
        disabled={disabled}
        placeholder={placeholder}
        model={currentModel}
        mode={mode}
        setMode={setMode}
        showAdvancedMode={showAdvancedMode}
        personas={personas}
        selectedPersona={selectedPersona}
        onPersonaChange={onPersonaChange}
        additionalControls={additionalControls}
        inputHelper={inputHelper}
      />
    </>
  )
}

// Comment: Separate input component to isolate re-renders
interface ChatInputProps {
  onSendMessage: (model: string, input: string) => void | Promise<void>
  onResetChat?: () => void | Promise<void>
  isLoading?: boolean
  disabled?: boolean
  placeholder?: string
  model: string
  mode: 'normal' | 'advanced'
  setMode: (mode: 'normal' | 'advanced') => void
  showAdvancedMode?: boolean
  personas?: string[]
  selectedPersona?: string
  onPersonaChange?: (persona: string) => void
  additionalControls?: React.ReactNode
  inputHelper?: React.ReactNode
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onResetChat,
  isLoading = false,
  disabled = false,
  placeholder = 'Ask about this table...',
  model,
  mode,
  setMode,
  showAdvancedMode = false,
  personas = [],
  selectedPersona,
  onPersonaChange,
  additionalControls,
  inputHelper,
}) => {
  const [input, setInput] = useState('')

  const handleSend = () => {
    if (input.trim() && !isLoading && !disabled) {
      onSendMessage(model, input)
      setInput('')
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
      

      {/* Input row */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={3}
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading || disabled}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || isLoading || disabled}
          sx={{ color: (!input.trim() || isLoading || disabled) ? 'action.disabled' : '#00b2ff' }}
        >
          <SendIcon />
        </IconButton>
      </Box>

      {/* Helper line directly under the input (e.g. context on-ramp link) */}
      {inputHelper && (
        <Box sx={{ mb: 1.5, mt: -1 }}>
          {inputHelper}
        </Box>
      )}

      {/* Controls row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center' }}>
        {additionalControls}
        {
          personas.length > 0 && onPersonaChange && (
            <Box sx={{ flex: 1, minWidth: '120px', fontSize: '0.70rem' }}>
              <FormControl fullWidth size="small">
                <InputLabel id="persona-select-label" sx={{ fontSize: '0.70rem' }}>Persona</InputLabel>
                <Select
                  labelId="persona-select-label"
                  id="persona-select"
                  value={selectedPersona}
                  label="Persona"
                  onChange={(e) => onPersonaChange(e.target.value)}
                  disabled={isLoading || disabled}
                  sx={{ fontSize: '0.70rem' }}
                  MenuProps={{
                    PaperProps: {
                      sx: { fontSize: '0.70rem' }
                    }
                  }}
                >
                  {personas.map((persona) => (
                    <MenuItem key={persona} value={persona} sx={{ fontSize: '0.70rem' }}>
                      {persona}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )
        }
        {
          showAdvancedMode && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color={mode === 'normal' ? 'text.primary' : 'text.secondary'}>Normal</Typography>
              <Switch
                size="small"
                checked={mode === 'advanced'}
                onChange={(event) => setMode(event.target.checked ? 'advanced' : 'normal')}
                inputProps={{ 'aria-label': 'Toggle advanced mode' }}
              />
              <Typography variant="caption" color={mode === 'advanced' ? 'text.primary' : 'text.secondary'}>Advanced</Typography>
            </Box>
          )
        }
        {/* Reset button - shown when header is hidden and there are chat messages */}
        {onResetChat && (
          <Button
            variant="text"
            size="small"
            onClick={() => onResetChat?.()}
            disabled={isLoading}
            sx={{
              textTransform: 'none',
              fontSize: '0.70rem',
              padding: '3px 6px',
              color: 'primary.main',
              textDecoration: 'underline',
              minWidth: 'auto',
              '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' }
            }}
          >
            reset chat
          </Button>
        )}
      </Box>

      {/* Disclaimer */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', pt: 0.5, color: 'text.secondary' }}>
        <Typography variant="caption">AI models can make mistakes - double check all outputs</Typography>
      </Box>
    </Box>
  )
}

export default ChatSession
